/**
 * Advanced Filters for Ministers List Report
 */

import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Filter, X, Download } from "lucide-react";
import type { District } from "@/models/district";
import type { Region } from "@/models/region";
import type { Degree } from "@/models/degree";
import type { MinistersListReportFilters } from "../types";
import { CredentialType, CredentialRequestStatus } from "@/models/credentialRequest";

interface ReportFiltersProps {
  filters: MinistersListReportFilters;
  onFilterChange: <K extends keyof MinistersListReportFilters>(
    key: K,
    value: MinistersListReportFilters[K]
  ) => void;
  onToggleCredentialType: (type: CredentialType) => void;
  onToggleStatus: (status: CredentialRequestStatus) => void;
  onReset: () => void;
  onExport: () => void;
  districts: District[];
  regions: Region[];
  degrees: Degree[];
  isLoading?: boolean;
  totalResults?: number;
}

const CREDENTIAL_TYPES = [
  { value: CredentialType.MINISTERIAL, label: "Ministerial" },
  { value: CredentialType.INSTITUTION, label: "Institución" },
  { value: CredentialType.FUNCTION, label: "Función" },
];

const ALL_STATUSES = [
  { value: CredentialRequestStatus.DRAFT, label: "Borrador" },
  { value: CredentialRequestStatus.SUBMITTED, label: "Enviado" },
  { value: CredentialRequestStatus.APPROVED, label: "Aprobado" },
  { value: CredentialRequestStatus.PRINTED, label: "Impreso" },
  { value: CredentialRequestStatus.DELIVERED, label: "Entregado" },
  { value: CredentialRequestStatus.REJECTED, label: "Rechazado" },
];

export function ReportFilters({
  filters,
  onFilterChange,
  onToggleCredentialType,
  onToggleStatus,
  onReset,
  onExport,
  districts,
  regions,
  degrees,
  isLoading = false,
  totalResults = 0,
}: ReportFiltersProps) {
  // Filter regions by selected district
  const filteredRegions = filters.districtId
    ? regions.filter((r) => r.district_id === filters.districtId)
    : regions;

  const activeFiltersCount =
    (filters.districtId ? 1 : 0) +
    (filters.regionId ? 1 : 0) +
    (filters.degreeId ? 1 : 0);

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4">
      {/* Title and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="size-5" />
          <h3 className="font-semibold">Filtros</h3>
          {activeFiltersCount > 0 && (
            <Badge variant="secondary">{activeFiltersCount}</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {totalResults} resultado{totalResults !== 1 ? "s" : ""}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={onExport}
            disabled={isLoading || totalResults === 0}
          >
            <Download className="mr-2 size-4" />
            Exportar Excel
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            disabled={isLoading}
          >
            <X className="mr-2 size-4" />
            Limpiar
          </Button>
        </div>
      </div>

      {/* Filter Grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Date From */}
        <div className="space-y-2">
          <Label htmlFor="dateFrom">Fecha Desde</Label>
          <Input
            id="dateFrom"
            type="date"
            value={filters.dateFrom}
            onChange={(e) => onFilterChange("dateFrom", e.target.value)}
            disabled={isLoading}
          />
        </div>

        {/* Date To */}
        <div className="space-y-2">
          <Label htmlFor="dateTo">Fecha Hasta</Label>
          <Input
            id="dateTo"
            type="date"
            value={filters.dateTo}
            onChange={(e) => onFilterChange("dateTo", e.target.value)}
            disabled={isLoading}
          />
        </div>

        {/* Credential Types (Multi-select Popover) */}
        <div className="space-y-2">
          <Label>Tipo de Credencial</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between"
                disabled={isLoading}
              >
                {filters.credentialTypes.length === 0
                  ? "Seleccionar..."
                  : filters.credentialTypes.length === CREDENTIAL_TYPES.length
                  ? "Todos"
                  : `${filters.credentialTypes.length} seleccionado${filters.credentialTypes.length > 1 ? "s" : ""}`}
                <Filter className="ml-2 size-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56">
              <div className="space-y-2">
                {CREDENTIAL_TYPES.map((type) => (
                  <div key={type.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`cred-type-${type.value}`}
                      checked={filters.credentialTypes.includes(type.value)}
                      onCheckedChange={() => onToggleCredentialType(type.value)}
                    />
                    <Label
                      htmlFor={`cred-type-${type.value}`}
                      className="cursor-pointer text-sm font-normal"
                    >
                      {type.label}
                    </Label>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Status (Multi-select Popover) */}
        <div className="space-y-2">
          <Label>Estado</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between"
                disabled={isLoading}
              >
                {filters.statuses.length === 0
                  ? "Seleccionar..."
                  : filters.statuses.length === ALL_STATUSES.length
                  ? "Todos"
                  : `${filters.statuses.length} seleccionado${filters.statuses.length > 1 ? "s" : ""}`}
                <Filter className="ml-2 size-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56">
              <div className="space-y-2">
                {ALL_STATUSES.map((status) => (
                  <div key={status.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`status-${status.value}`}
                      checked={filters.statuses.includes(status.value)}
                      onCheckedChange={() => onToggleStatus(status.value)}
                    />
                    <Label
                      htmlFor={`status-${status.value}`}
                      className="cursor-pointer text-sm font-normal"
                    >
                      {status.label}
                    </Label>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* District */}
        <div className="space-y-2">
          <Label htmlFor="districtId">Distrito</Label>
          <Select
            value={filters.districtId || "all"}
            onValueChange={(value) =>
              onFilterChange("districtId", value === "all" ? null : value)
            }
            disabled={isLoading}
          >
            <SelectTrigger id="districtId">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {districts.map((district) => (
                <SelectItem key={district.id} value={district.id}>
                  {district.name || district.code || "Sin nombre"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Region */}
        <div className="space-y-2">
          <Label htmlFor="regionId">Región</Label>
          <Select
            value={filters.regionId || "all"}
            onValueChange={(value) =>
              onFilterChange("regionId", value === "all" ? null : value)
            }
            disabled={isLoading || !filters.districtId}
          >
            <SelectTrigger id="regionId">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {filteredRegions.map((region) => (
                <SelectItem key={region.id} value={region.id}>
                  {region.name || region.code || "Sin nombre"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Degree */}
        <div className="space-y-2">
          <Label htmlFor="degreeId">Grado</Label>
          <Select
            value={filters.degreeId || "all"}
            onValueChange={(value) =>
              onFilterChange("degreeId", value === "all" ? null : value)
            }
            disabled={isLoading}
          >
            <SelectTrigger id="degreeId">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {degrees.map((degree) => (
                <SelectItem key={degree.id} value={degree.id}>
                  {degree.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
